import type {
  User,
  Category,
  Question,
  Option,
  CreateSubmissionRequest,
  SubmissionDetailsDTO,
  SubmissionDetailsOverview,
  SubmissionListItemDTO,
  AIPathType,
  AssessmentFullResults,
  GeminiAdminReport,
} from '../contracts/types';
import * as Sentry from '@sentry/react';

interface AssessmentStructure {
  categories: Category[];
  questions: Question[];
  options: Option[];
}

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toStringSafe = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const toOptionalStringSafe = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const toNullableStringSafe = (value: unknown): string | null | undefined =>
  value === null ? null : typeof value === 'string' ? value : undefined;

const toNumberSafe = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const toBooleanSafe = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const addValidationBreadcrumb = (endpoint: string, details: string) => {
  Sentry.addBreadcrumb({
    category: 'api.validation',
    message: 'Normalized unexpected API payload shape',
    level: 'warning',
    data: { endpoint, details },
  });
};

const normalizeUser = (raw: unknown): User => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/auth/verify', 'Response is not an object');
    throw new Error('Invalid user profile response');
  }
  const id = toStringSafe(raw.id).trim();
  if (!id) {
    addValidationBreadcrumb('/auth/verify', 'Missing user id');
    throw new Error('Invalid user profile response');
  }
  return {
    id,
    isAdmin: typeof raw.isAdmin === 'boolean' ? raw.isAdmin : undefined,
    email: toOptionalStringSafe(raw.email),
    companyName: toOptionalStringSafe(raw.companyName),
  };
};

const normalizeAssessmentStructure = (raw: unknown): AssessmentStructure => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/assessment', 'Response is not an object');
    throw new Error('Invalid assessment structure response');
  }
  const categories = Array.isArray(raw.categories) ? raw.categories : [];
  const questions = Array.isArray(raw.questions) ? raw.questions : [];
  const options = Array.isArray(raw.options) ? raw.options : [];

  const normalizedCategories: Category[] = categories
    .filter(isRecord)
    .map((item) => ({
      id: toNumberSafe(item.id),
      name: toStringSafe(item.name),
      namePl: toNullableStringSafe(item.namePl),
      weight: toNumberSafe(item.weight),
      pathId: toNumberSafe(item.pathId, 0) as AIPathType,
    }))
    .filter((item) => item.id > 0 && item.name.length > 0);

  const normalizedQuestions: Question[] = questions
    .filter(isRecord)
    .map((item) => ({
      id: toNumberSafe(item.id),
      categoryId: toNumberSafe(item.categoryId),
      text: toStringSafe(item.text),
      textPl: toNullableStringSafe(item.textPl),
      weight: toNumberSafe(item.weight),
    }))
    .filter((item) => item.id > 0 && item.categoryId > 0 && item.text.length > 0);

  const normalizedOptions: Option[] = options
    .filter(isRecord)
    .map((item) => ({
      id: toNumberSafe(item.id),
      questionId: toNumberSafe(item.questionId),
      text: toStringSafe(item.text),
      textPl: toNullableStringSafe(item.textPl),
      points: toNumberSafe(item.points),
    }))
    .filter((item) => item.id > 0 && item.questionId > 0 && item.text.length > 0);

  if (
    normalizedCategories.length !== categories.length
    || normalizedQuestions.length !== questions.length
    || normalizedOptions.length !== options.length
  ) {
    addValidationBreadcrumb('/assessment', 'Dropped invalid categories/questions/options');
  }

  return {
    categories: normalizedCategories,
    questions: normalizedQuestions,
    options: normalizedOptions,
  };
};

const normalizeSubmissionDetails = (raw: unknown): SubmissionDetailsDTO => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/assessment/submit', 'Response is not an object');
    throw new Error('Invalid submission response');
  }
  return {
    submissionId: toNumberSafe(raw.submissionId),
    overallScore: toNumberSafe(raw.overallScore),
    userId: toStringSafe(raw.userId),
    aiPath: raw.aiPath == null ? null : (toNumberSafe(raw.aiPath, 0) as AIPathType),
    companyName: toStringSafe(raw.companyName),
    companyEmail: toStringSafe(raw.companyEmail),
    answers: Array.isArray(raw.answers) ? raw.answers as SubmissionDetailsDTO['answers'] : [],
    questions: Array.isArray(raw.questions) ? raw.questions as SubmissionDetailsDTO['questions'] : [],
    options: Array.isArray(raw.options) ? raw.options as SubmissionDetailsDTO['options'] : [],
    categories: Array.isArray(raw.categories) ? raw.categories as SubmissionDetailsDTO['categories'] : [],
  };
};

const normalizeSubmissionOverview = (raw: unknown): SubmissionDetailsOverview => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/admin/submissions/:id', 'Submission overview is not an object');
    throw new Error('Invalid submission overview response');
  }
  return {
    companyName: toStringSafe(raw.companyName),
    overallScore: toNumberSafe(raw.overallScore),
    primaryGap: toStringSafe(raw.primaryGap),
    radarData: (Array.isArray(raw.radarData) ? raw.radarData : [])
      .filter(isRecord)
      .map((point) => ({
        subject: toStringSafe(point.subject),
        A: toNumberSafe(point.A),
        full: toNumberSafe(point.full, 100),
      })),
  };
};

const normalizeResults = (raw: unknown): AssessmentFullResults => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/submissions/:id/results', 'Response is not an object');
    throw new Error('Invalid results response');
  }
  const analysisRaw = isRecord(raw.analysisAI) ? raw.analysisAI : {};
  const businessCasesRaw = Array.isArray(analysisRaw.businessCases) ? analysisRaw.businessCases : [];

  const normalized: AssessmentFullResults = {
    overallScore: toNumberSafe(raw.overallScore),
    marketInsight: toStringSafe(raw.marketInsight),
    marketStats: {
      average: toNumberSafe(isRecord(raw.marketStats) ? raw.marketStats.average : undefined),
      best: toNumberSafe(isRecord(raw.marketStats) ? raw.marketStats.best : undefined),
    },
    radarData: (Array.isArray(raw.radarData) ? raw.radarData : [])
      .filter(isRecord)
      .map((point) => ({
        subject: toStringSafe(point.subject),
        A: toNumberSafe(point.A),
        full: toNumberSafe(point.full, 100),
      })),
    aiPath: toNumberSafe(raw.aiPath, 0) as AIPathType,
    analysisAI: {
      radarChartDescription: toStringSafe(analysisRaw.radarChartDescription),
      aiEngineAudit: toStringSafe(analysisRaw.aiEngineAudit),
      criticalFailurePoints: toStringSafe(analysisRaw.criticalFailurePoints),
      aiStatusOpportunity: toStringSafe(analysisRaw.aiStatusOpportunity),
      ifYouDoNothing: toStringSafe(analysisRaw.ifYouDoNothing),
      businessCases: businessCasesRaw
        .filter(isRecord)
        .map((item) => ({
          task: toStringSafe(item.task),
          action: toStringSafe(item.action),
        }))
        .filter((item) => item.task.length > 0 || item.action.length > 0),
    },
    reportPreparing: toBooleanSafe(raw.reportPreparing, false),
  };

  if (!isRecord(raw.analysisAI) || !Array.isArray(raw.radarData)) {
    addValidationBreadcrumb('/submissions/:id/results', 'Applied fallback normalization for missing analysis/radar');
  }

  return normalized;
};

const normalizeAdminSubmissions = (raw: unknown): SubmissionListItemDTO[] => {
  if (!Array.isArray(raw)) {
    addValidationBreadcrumb('/admin/submissions', 'Response is not an array');
    throw new Error('Invalid admin submissions response');
  }
  const normalized = raw
    .filter(isRecord)
    .map((item) => ({
      id: toNumberSafe(item.id),
      createdAt: toStringSafe(item.createdAt),
      score: toNumberSafe(item.score),
      companyName: toStringSafe(item.companyName),
    }))
    .filter((item) => item.id > 0 && item.createdAt.length > 0);
  if (normalized.length !== raw.length) {
    addValidationBreadcrumb('/admin/submissions', 'Dropped invalid submission rows');
  }
  return normalized;
};

const normalizeAdminAudit = (raw: unknown): GeminiAdminReport => {
  if (!isRecord(raw)) {
    addValidationBreadcrumb('/admin/audit/:id', 'Response is not an object');
    throw new Error('Invalid admin audit response');
  }
  return {
    salesStrategyOffering: toStringSafe(raw.salesStrategyOffering),
  };
};

const API_URL = (import.meta.env.VITE_API_URL?.trim() || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : 'https://readiness-api.seargin.com/api')).replace(/\/+$/, '');
const CSRF_COOKIE_NAME = import.meta.env.VITE_CSRF_COOKIE_NAME || 'aid_csrf';
const CONTACT_FUNCTION_URL = import.meta.env.VITE_CONTACT_FUNCTION_URL?.trim() || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || '';
const MAX_COMPANY_NAME_LENGTH = 120;
const MAX_EMAIL_LENGTH = 320;
const MAX_PHONE_LENGTH = 32;
const MAX_CONTACT_MESSAGE_LENGTH = 5_000;
const MOBILE_AUTH_GRACE_MS = 550;
let sessionRefreshPromise: Promise<void> | null = null;

const isLikelyMobileClient = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|mobile|windows phone|webos/i.test(ua);
};

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getCookieValue = (name: string): string | null => {
  const pairs = document.cookie ? document.cookie.split(';') : [];
  for (const pair of pairs) {
    const [k, ...rest] = pair.trim().split('=');
    if (k !== name) continue;
    const value = rest.join('=');
    if (!value) return null;
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
};

const createHeaders = (token?: string | null, includeCsrf = false) => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token && token !== '__cookie_session__') {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeCsrf) {
    const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return headers;
};

const sanitizeTextInput = (
  value: string,
  maxLength: number,
  opts?: { multiline?: boolean }
) => {
  const normalized = String(value || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(opts?.multiline ? /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g : /[\u0000-\u001F]/g, '')
    .trim();

  if (normalized.length > maxLength) {
    throw new Error('Payload too large.');
  }

  return normalized;
};

const sanitizeEmailInput = (value: string) => {
  const sanitized = sanitizeTextInput(value, MAX_EMAIL_LENGTH).toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized)) {
    throw new Error('Invalid input.');
  }
  return sanitized;
};

export type LegalConsentPayload = {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  legalVersion: string;
  acceptedAt: string;
};

export async function registerOrLogin(
  companyName: string,
  email: string,
  legalConsent?: LegalConsentPayload,
): Promise<void> {
  const safeCompanyName = sanitizeTextInput(companyName, MAX_COMPANY_NAME_LENGTH);
  const safeEmail = sanitizeEmailInput(email);
  if (!safeCompanyName) {
    throw new Error('Company name is required to continue.');
  }

  localStorage.setItem('pending_company_name', safeCompanyName);
  if (email) {
    localStorage.setItem('pending_company_email', safeEmail);
  }

  const response = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: safeEmail,
      companyName: safeCompanyName,
      legalConsent,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Server error: failed to send magic link');
  }
}

export async function fetchUserProfile(
  token?: string | null,
  companyName?: string,
  signal?: AbortSignal,
  refreshToken?: string | null,
): Promise<User> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/auth/verify`, {
      method: 'POST',
      headers: createHeaders(token),
      credentials: 'include',
      body: JSON.stringify({
        token: token && token !== '__cookie_session__' ? token : undefined,
        refreshToken: refreshToken || undefined,
        companyName,
      }),
      signal,
    });
  } catch (rawError) {
    const networkError = rawError as Error & { status?: number; code?: string; endpoint?: string };
    networkError.status = 0;
    networkError.code = networkError.code || 'AUTH_VERIFY_NETWORK_ERROR';
    networkError.endpoint = '/auth/verify';
    throw networkError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || 'Identity verification failed';
    const error = new Error(message) as Error & { status?: number; code?: string; endpoint?: string };
    error.status = response.status;
    error.endpoint = '/auth/verify';
    if (typeof errorData.code === 'string') {
      error.code = errorData.code;
    }
    throw error;
  }

  return normalizeUser(await response.json());
}

const refreshSessionSingleFlight = async (token?: string | null) => {
  if (sessionRefreshPromise) return sessionRefreshPromise;
  sessionRefreshPromise = (async () => {
    const pendingCompanyName = localStorage.getItem('pending_company_name') || undefined;
    await fetchUserProfile(token, pendingCompanyName);
  })().finally(() => {
    sessionRefreshPromise = null;
  });
  return sessionRefreshPromise;
};

const fetchWithAuthRecovery = async (
  input: string,
  init: RequestInit,
  opts?: { token?: string | null; allowAuthRecovery?: boolean }
) => {
  const response = await fetch(input, init);
  if (!opts?.allowAuthRecovery) return response;
  if (!(response.status === 401 || response.status === 403)) return response;
  if (!isLikelyMobileClient()) return response;

  const endpoint = (() => {
    try {
      return new URL(input).pathname || input;
    } catch {
      return input;
    }
  })();

  Sentry.addBreadcrumb({
    category: 'auth.mobile',
    message: 'Protected request unauthorized on mobile, attempting single-flight refresh',
    level: 'warning',
    data: { endpoint, status: response.status },
  });

  await sleep(MOBILE_AUTH_GRACE_MS);

  try {
    await refreshSessionSingleFlight(opts?.token);
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'auth.mobile',
      message: 'Single-flight mobile refresh failed',
      level: 'warning',
      data: {
        endpoint,
        status: response.status,
        error: String((error as { message?: string } | null)?.message || 'unknown'),
      },
    });
    return response;
  }

  Sentry.addBreadcrumb({
    category: 'auth.mobile',
    message: 'Single-flight mobile refresh succeeded, replaying request once',
    level: 'info',
    data: { endpoint },
  });

  return fetch(input, init);
};
export async function fetchAssessmentStructure(
  aiPath: AIPathType | null,
  token?: string | null,
  language: 'pl' | 'en' = 'en',
  signal?: AbortSignal,
): Promise<AssessmentStructure> {
  const params = new URLSearchParams();
  if (aiPath !== null) params.set('aiPath', String(aiPath));
  params.set('lang', language);
  const queryParam = params.toString() ? `?${params.toString()}` : '';

  let response: Response;
  try {
    response = await fetchWithAuthRecovery(`${API_URL}/assessment${queryParam}`, {
      method: 'GET',
      headers: createHeaders(token),
      credentials: 'include',
      signal,
    }, { token, allowAuthRecovery: true });
  } catch (rawError) {
    const networkError = rawError as Error & {
      status?: number;
      endpoint?: string;
      code?: string;
      details?: string;
    };
    networkError.status = 0;
    networkError.endpoint = '/assessment';
    networkError.code = networkError.code || 'ASSESSMENT_STRUCTURE_NETWORK_ERROR';
    networkError.details = networkError.details || networkError.message;
    throw networkError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error('Failed to fetch assessment structure') as Error & {
      status?: number;
      endpoint?: string;
      code?: string;
      details?: string;
    };
    error.status = response.status;
    error.endpoint = '/assessment';
    if (typeof errorData.code === 'string') {
      error.code = errorData.code;
    }
    if (typeof errorData.message === 'string') {
      error.details = errorData.message;
    } else if (typeof errorData.error === 'string') {
      error.details = errorData.error;
    }
    throw error;
  }
  return normalizeAssessmentStructure(await response.json());
}

export async function submitAssessment(
  payload: CreateSubmissionRequest,
  token?: string | null,
  signal?: AbortSignal,
): Promise<SubmissionDetailsDTO> {
  const safePayload: CreateSubmissionRequest = {
    ...payload,
    companyName: sanitizeTextInput(payload.companyName, MAX_COMPANY_NAME_LENGTH),
    companyEmail: sanitizeEmailInput(payload.companyEmail),
    answers: payload.answers.slice(0, 250).map((answer) => ({
      questionId: Number(answer.questionId),
      selectedOptionId: answer.selectedOptionId == null ? null : Number(answer.selectedOptionId),
    })),
  };

  const response = await fetchWithAuthRecovery(`${API_URL}/assessment/submit`, {
    method: 'POST',
    headers: createHeaders(token, true),
    credentials: 'include',
    body: JSON.stringify(safePayload),
    signal,
  }, { token, allowAuthRecovery: true });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.error || errorData.message || 'Failed to submit assessment';
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  return normalizeSubmissionDetails(await response.json());
}

export async function fetchFullResults(
  submissionId: number,
  token?: string | null,
  language: 'pl' | 'en' = 'en',
  cacheOnly = false,
  signal?: AbortSignal,
): Promise<AssessmentFullResults> {
  const params = new URLSearchParams();
  params.set('lang', language);
  if (cacheOnly) {
    params.set('cacheOnly', '1');
  }

  const response = await fetchWithAuthRecovery(`${API_URL}/submissions/${submissionId}/results?${params.toString()}`, {
    method: 'GET',
    headers: createHeaders(token),
    credentials: 'include',
    signal,
  }, { token, allowAuthRecovery: true });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.error || errorData.message || 'Failed to fetch maturity results';
    const error = new Error(message) as Error & { status?: number; code?: string };
    error.status = response.status;
    if (typeof errorData.code === 'string') {
      error.code = errorData.code;
    }
    throw error;
  }

  return normalizeResults(await response.json());
}

export async function fetchAdminSubmissions(
  token?: string | null,
  signal?: AbortSignal,
): Promise<SubmissionListItemDTO[]> {
  const response = await fetchWithAuthRecovery(`${API_URL}/admin/submissions`, {
    method: 'GET',
    headers: createHeaders(token),
    credentials: 'include',
    signal,
  }, { token, allowAuthRecovery: true });

  if (!response.ok) throw new Error('Unauthorized: admin access required');
  return normalizeAdminSubmissions(await response.json());
}

export async function fetchSubmissionDetails(
  id: number,
  token?: string | null,
  signal?: AbortSignal,
): Promise<{ data: SubmissionDetailsOverview }> {
  const response = await fetchWithAuthRecovery(`${API_URL}/admin/submissions/${id}`, {
    method: 'GET',
    headers: createHeaders(token),
    credentials: 'include',
    signal,
  }, { token, allowAuthRecovery: true });

  if (!response.ok) throw new Error('Failed to fetch submission details');
  const raw = await response.json();
  if (isRecord(raw) && 'data' in raw) {
    return {
      data: normalizeSubmissionOverview((raw as { data?: unknown }).data),
    };
  }
  addValidationBreadcrumb('/admin/submissions/:id', 'Missing data wrapper in submission details response');
  return {
    data: normalizeSubmissionOverview(raw),
  };
}

export async function fetchAdminAudit(
  id: number,
  token?: string | null,
  language: 'pl' | 'en' = 'en',
  cacheOnly = false,
  signal?: AbortSignal,
): Promise<GeminiAdminReport> {
  const params = new URLSearchParams();
  params.set('lang', language);
  if (cacheOnly) params.set('cacheOnly', '1');

  const response = await fetchWithAuthRecovery(`${API_URL}/admin/audit/${id}?${params.toString()}`, {
    method: 'POST',
    headers: createHeaders(token, true),
    credentials: 'include',
    signal,
  }, { token, allowAuthRecovery: true });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.error || errorData.message || 'AI audit request failed';
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  return normalizeAdminAudit(await response.json());
}


type SendContactRequestPayload = {
  submissionId: number;
  companyName: string;
  workEmail: string;
  phone?: string;
  message?: string;
};

export async function sendResultsContactRequest(
  payload: SendContactRequestPayload,
  token?: string | null,
  signal?: AbortSignal,
): Promise<void> {
  const safePayload: SendContactRequestPayload = {
    submissionId: Number(payload.submissionId),
    companyName: sanitizeTextInput(payload.companyName, MAX_COMPANY_NAME_LENGTH),
    workEmail: sanitizeEmailInput(payload.workEmail),
    phone: sanitizeTextInput(payload.phone || '', MAX_PHONE_LENGTH),
    message: sanitizeTextInput(payload.message || '', MAX_CONTACT_MESSAGE_LENGTH, { multiline: true }),
  };

  const hasJwtToken = Boolean(token && token !== '__cookie_session__');
  const canUseEdgeFunction = CONTACT_FUNCTION_URL.length > 0 && SUPABASE_ANON_KEY.length > 0 && hasJwtToken;
  const targetUrl = canUseEdgeFunction ? CONTACT_FUNCTION_URL : `${API_URL}/contact/request`;

  const headers: HeadersInit = canUseEdgeFunction
    ? {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
      }
    : createHeaders(token, true);

  const response = await fetchWithAuthRecovery(targetUrl, {
    method: 'POST',
    headers,
    credentials: canUseEdgeFunction ? 'omit' : 'include',
    body: JSON.stringify(safePayload),
    signal,
  }, { token, allowAuthRecovery: !canUseEdgeFunction });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to send contact request');
  }
}
export async function logoutSession(): Promise<void> {
  await fetch(`${API_URL}/auth/logout`, {
    method: 'POST',
    headers: createHeaders(undefined, true),
    credentials: 'include',
  });
}
